import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Stack, useClickOutside, useDropdownPlacement } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { createPortal } from "react-dom";

import style from "./MenuPortal.module.css";

export const OverlayContext = React.createContext<{
  overlayRef: RefObject<HTMLDivElement | null> | undefined;
}>({ overlayRef: undefined });

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const overlayRef = useRef(null);
  return (
    <>
      <OverlayContext.Provider value={{ overlayRef }}>{children}</OverlayContext.Provider>
      <div ref={overlayRef}></div>
    </>
  );
};

type MenuPortalChildren = {
  Item: typeof MenuItem;
  Link: typeof MenuLink;
  Divider: typeof MenuDivider;
};

interface MenuPortalProps {
  children: ReactNode;
  /**
   * The element that triggers the menu portal.
   * Must be a React element that accepts both `onClick` and `ref` props.
   * The element must be able to forward refs as the positioning system depends on it.
   * @example
   * <Button ref={ref} onClick={onClick}>Trigger</Button>
   */
  activator: ReactElement<{
    onClick: React.MouseEventHandler;
    ref: React.RefAttributes<HTMLElement>["ref"];
    children: ReactNode;
  }>;
  onOpen?: () => void;
  onClose?: () => void;
  onClick?: React.MouseEventHandler;
  matchActivatorWidth?: boolean;
  dropdownPosition?: "left" | "right";
  className?: string;
  menuClassName?: string;
  portalTarget?: HTMLElement;
}

export const MenuPortal = forwardRef<{ close: () => void }, MenuPortalProps>(
  (
    {
      children,
      className,
      activator,
      matchActivatorWidth,
      menuClassName,
      onOpen,
      onClose,
      onClick,
      dropdownPosition = "right",
      portalTarget,
    },
    ref
  ) => {
    const { overlayRef } = useContext(OverlayContext);
    const [open, setOpen] = useState(false);
    const clickOutsideTarget = useRef(null);
    const id = useId();
    const activatorRef = useRef<HTMLDivElement>(null);

    const closeCallback = useCallback(() => {
      setOpen(false);
      if (open) onClose?.();
    }, [open, setOpen, onClose]);

    const openCallback = useCallback(() => {
      setOpen(true);
      if (!open) onOpen?.();

      const closeMenu = () => {
        closeCallback();
        document.removeEventListener("scroll", closeMenu);
      };
      document.addEventListener("scroll", closeMenu);

      return () => document.removeEventListener("scroll", closeMenu);
    }, [open, setOpen, onOpen, closeCallback]);

    const menuRef = useRef<HTMLDivElement>(null);

    const dropdownStyle = useDropdownPlacement(open, activatorRef, menuRef, dropdownPosition);

    useClickOutside(clickOutsideTarget.current, closeCallback);
    useImperativeHandle(
      ref,
      () => ({
        close: closeCallback,
      }),
      [closeCallback]
    );

    const resolvedPortalTarget = portalTarget ?? overlayRef?.current ?? document.body;

    return (
      <div ref={clickOutsideTarget} className={classNames(style.menuWrapper, className)}>
        {cloneElement(activator, {
          ref: activatorRef,
          onClick: (event: React.MouseEvent) => {
            if (open) {
              closeCallback();
            } else {
              openCallback();
            }

            activator.props.onClick?.(event);
          },
        })}
        {open &&
          resolvedPortalTarget &&
          createPortal(
            <Stack
              role="menu"
              style={dropdownStyle}
              className={classNames(
                style.menu,
                matchActivatorWidth && style.fullWidth,
                menuClassName
              )}
              direction={"column"}
              onClick={onClick}
              ref={menuRef}
            >
              {Children.map(children, (child) => {
                if (
                  child &&
                  isValidElement<{ closeOnClick?: boolean }>(child) &&
                  child.props.closeOnClick
                ) {
                  const c = child as MenuPortalProps["activator"];
                  return cloneElement(
                    c,
                    {
                      onClick: (event: React.MouseEvent) => {
                        if (open) {
                          closeCallback();
                        } else {
                          openCallback();
                        }
                        c.props.onClick?.(event);
                      },
                    },
                    c.props.children
                  );
                }

                return child;
              })}
            </Stack>,
            resolvedPortalTarget,
            id
          )}
      </div>
    );
  }
) as React.ForwardRefExoticComponent<
  MenuPortalProps & React.RefAttributes<{ close: () => void }>
> & {
  [key in keyof MenuPortalChildren]: MenuPortalChildren[key];
};
MenuPortal.displayName = "Menu";

const MenuItem: React.FC<{
  children: ReactNode;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  closeOnClick?: boolean;
  className?: string;
}> = ({ children, disabled, onClick, className }) => {
  return (
    <button
      role="menuitem"
      className={classNames(style.menuItem, className)}
      disabled={disabled}
      onClick={(event: React.MouseEvent) => onClick?.(event)}
    >
      {children}
    </button>
  );
};
MenuPortal.Item = MenuItem;

const MenuLink: React.FC<
  {
    children: ReactNode;
    closeOnClick?: boolean;
  } & React.AnchorHTMLAttributes<HTMLAnchorElement>
> = ({ children, closeOnClick: _, ...attrs }) => {
  return (
    <a role="menuitem" {...attrs} className={classNames(style.menuItem)}>
      {children}
    </a>
  );
};
MenuPortal.Link = MenuLink;

const MenuDivider: React.FC = () => {
  return <hr role="separator" className={style.menuDivider}></hr>;
};
MenuPortal.Divider = MenuDivider;
